import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import {
  VendorOrder,
  VendorOrderFile,
} from '../../../../../shared/models/vendor-order.model';
import { S3FilesService } from '../../../../../shared/services/s3files.service';
import {
  S3DestinationFolder,
  TranslationFileType,
} from '../../../../../shared/models/s3-destination-folder';
import { QUALIFYING_STATUSES_FOR_REVISED_DTP, LANGUAGES } from '../../../../../shared/config/constants';
import {
  LanguageProgressItem,
  TranslatedFileDisplayItem,
} from './translated-files.models';
import { ToastService } from '../../../../../shared/services/toast.service';
import { VendorOrdersService } from '../services/vendor-orders.service';
import JSZip, { JSZipObject } from 'jszip';

type FileTableSortScope = 'basic' | 'dtp';

interface DownloadCandidate {
  localeId: string;
  s3Key: string;
  originalName: string;
}

const COMPLETION_ATTEMPT_COOLDOWN_MS = 60_000;
const completionAttemptByJob = new Map<string, number>();

@Component({
  selector: 'app-translated-files',
  standalone: true,
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
  ],
  templateUrl: './translated-files.component.html',
  styleUrl: './translated-files.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranslatedFilesComponent implements OnInit {
  static resetCompletionAttemptStateForTests(): void {
    completionAttemptByJob.clear();
  }

  detail = input.required<VendorOrder>();
  readonly jobCompleted = output<void>();

  private readonly s3FilesService = inject(S3FilesService);
  private readonly toastService = inject(ToastService);
  private readonly vendorOrdersService = inject(VendorOrdersService);

  readonly fileSortState = signal<Map<string, { column: 'name' | 'size'; descending: boolean }>>(new Map());
  readonly isDownloadingAll = signal(false);

  readonly s3TargetFiles = signal<Map<string, { displaySize: string }>>(new Map());
  readonly s3TargetFilesLoaded = signal(false);
  private isMarkingCompletion = signal(false);

  constructor() {
    effect(() => {
      const total = this.totalFiles();
      const delivered = this.deliveredFiles();
      const job = this.detail();
      const status = job.jobStatus ?? '';
      const jobUid = job.jobUid ?? '';

      if (
        jobUid &&
        !this.isMarkingCompletion() &&
        this.canAttemptCompletion(jobUid) &&
        this.s3TargetFilesLoaded() &&
        this.hasLanguageData() &&
        total > 0 &&
        delivered === total &&
        this.canAutoMarkJobCompleted() &&
        status !== 'COMPLETED' &&
        status !== 'CANCELLED'
      ) {
        completionAttemptByJob.set(jobUid, Date.now());
        this.isMarkingCompletion.set(true);
        void this.markJobAsCompleted();
      }
    });
  }

  private canAttemptCompletion(jobUid: string): boolean {
    const lastAttempt = completionAttemptByJob.get(jobUid);
    if (lastAttempt === undefined) {
      return true;
    }

    const elapsed = Date.now() - lastAttempt;
    if (elapsed > COMPLETION_ATTEMPT_COOLDOWN_MS) {
      completionAttemptByJob.delete(jobUid);
      return true;
    }

    return false;
  }

  private canAutoMarkJobCompleted(): boolean {
    const files = this.detail().files ?? [];
    const s3Files = this.s3TargetFiles();
    const job = this.detail();
    const useRevised = this.shouldUseRevisedClassification(job.jobStatus ?? '', !!job.vendor);

    for (const file of files) {
      if (file.fileType === 'attachment') {
        continue;
      }

      const isDtp = useRevised
        ? (file.isDtpFileRevised ?? file.isDtpFile ?? false)
        : (file.isDtpFile ?? false);
      const isDtpRevised = useRevised && file.isDtpFileRevised === true;

      const s3Key = isDtp ? file.translatedFileUploadName : file.uploadName;
      const s3Entry = s3Key ? s3Files.get(s3Key) : undefined;

      const isBasicUnchanged = !isDtp &&
        this.hasValue(file.originalName) &&
        this.hasValue(file.uploadName) &&
        file.originalName === file.uploadName;

      if (isBasicUnchanged) {
        return false;
      }

      if (isDtpRevised) {
        // Backend status is authoritative for revised DTP completion.
        if (file.status !== 'COMPLETED') {
          return false;
        }
      } else if (!s3Entry) {
        return false;
      }
    }

    return true;
  }

  async ngOnInit(): Promise<void> {
    await this.loadS3TargetFiles();
  }

  private async loadS3TargetFiles(): Promise<void> {
    try {
      const response = await this.s3FilesService.getFilesInFolderWithSize(
        TranslationFileType.TARGET,
        this.detail().jobUid!,
      );
      const sizeMap = new Map<string, { displaySize: string }>();
      for (const file of response.files) {
        sizeMap.set(file.name, {
          displaySize: this.formatFileSize(file.size),
        });
      }
      this.s3TargetFiles.set(sizeMap);
    } catch {
    } finally {
      this.s3TargetFilesLoaded.set(true);
    }
  }

  readonly languageProgress = computed<LanguageProgressItem[]>(() => {
    const job = this.detail();
    const allFiles: VendorOrderFile[] = job.files ?? [];
    const targetLocales = job.targetLocales ?? [];
    const s3Files = this.s3TargetFiles();

    const localeLookup = LANGUAGES as Record<string, string>;
    const localeNameMap = new Map<string, string>();

    for (const locale of targetLocales) {
      localeNameMap.set(locale.code, locale.name);
    }

    const sourceLocale = job.sourceLocaleId ?? allFiles[0]?.sourceLocaleId;
    for (const file of allFiles) {
      if (file.localeId && file.localeId !== sourceLocale && !localeNameMap.has(file.localeId)) {
        const name = file.target ?? localeLookup[file.localeId] ?? file.localeId;
        localeNameMap.set(file.localeId, name);
      }
    }

    const orderTargetLocales = job.targetLocaleId ?? [];
    for (const code of orderTargetLocales) {
      if (!localeNameMap.has(code)) {
        localeNameMap.set(code, localeLookup[code] ?? code);
      }
    }

    const perLocaleFiles = allFiles.filter(
      (f) => f.localeId && f.localeId !== sourceLocale && localeNameMap.has(f.localeId),
    );

    const jobStatus = job.jobStatus ?? '';
    const hasVendor = !!job.vendor;
    const useRevised = this.shouldUseRevisedClassification(jobStatus, hasVendor);

    const result: LanguageProgressItem[] = [];

    if (perLocaleFiles.length > 0) {
      const grouped = new Map<string, VendorOrderFile[]>();

      for (const file of perLocaleFiles) {
        const localeId = file.localeId!;
        const fileKey = `${file.name}::${localeId}`;

        if (!grouped.has(localeId)) {
          grouped.set(localeId, []);
        }

        const localeFiles = grouped.get(localeId)!;
        const existingIdx = localeFiles.findIndex((f) => `${f.name}::${f.localeId}` === fileKey);

        if (existingIdx === -1) {
          localeFiles.push(file);
        } else {
          const existing = localeFiles[existingIdx];
          const existingComplete = (existing.translationCompleteDate ?? 0) > 0;
          const newComplete = (file.translationCompleteDate ?? 0) > 0;

          if (newComplete && !existingComplete) {
            localeFiles[existingIdx] = file;
          } else if (!existingComplete && !newComplete && file.fileType !== 'attachment') {
            localeFiles[existingIdx] = file;
          }
        }
      }

      for (const [localeId, langFiles] of grouped) {
        const langName = localeNameMap.get(localeId) ?? localeId;
        const { basicFiles, dtpFiles, deliveredCount } = this.buildFileItems(
          langFiles, useRevised, s3Files, localeId,
        );

        result.push({
          name: langName,
          code: localeId,
          deliveredCount,
          totalCount: langFiles.length,
          isComplete: deliveredCount === langFiles.length && langFiles.length > 0,
          basicFiles,
          dtpFiles,
        });
      }
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  });

  readonly totalFiles = computed(() => {
    return this.languageProgress().reduce((sum, lang) => sum + lang.totalCount, 0);
  });

  readonly deliveredFiles = computed(() => {
    return this.languageProgress().reduce((sum, lang) => sum + lang.deliveredCount, 0);
  });

  readonly completionPercentage = computed(() => {
    const total = this.totalFiles();
    if (total === 0) return '0.0';
    return ((this.deliveredFiles() / total) * 100).toFixed(1);
  });

  readonly hasAnyDeliveredFiles = computed(() => this.deliveredFiles() > 0);

  readonly hasLanguageData = computed(() => this.languageProgress().length > 0);

  toggleSort(
    localeCode: string,
    column: 'name' | 'size' = 'name',
    scope: FileTableSortScope = 'basic',
    files?: TranslatedFileDisplayItem[],
  ): void {
    if (column === 'size' && files && !this.hasSortableSize(files)) {
      return;
    }

    const stateKey = this.getSortStateKey(localeCode, scope);
    this.fileSortState.update((map) => {
      const updated = new Map(map);
      const current = updated.get(stateKey);
      if (current?.column === column) {
        updated.set(stateKey, { column, descending: !current.descending });
      } else {
        updated.set(stateKey, { column, descending: false });
      }
      return updated;
    });
  }

  getSortedFiles(
    files: TranslatedFileDisplayItem[],
    localeCode: string,
    scope: FileTableSortScope = 'basic',
  ): TranslatedFileDisplayItem[] {
    const state = this.fileSortState().get(this.getSortStateKey(localeCode, scope));
    if (!state) return this.sortFilesByName(files, false);
    if (state.column === 'size') {
      return this.sortFilesBySize(files, state.descending);
    }
    return this.sortFilesByName(files, state.descending);
  }

  isSortDescending(localeCode: string, scope: FileTableSortScope = 'basic'): boolean {
    return this.fileSortState().get(this.getSortStateKey(localeCode, scope))?.descending ?? false;
  }

  getSortColumn(localeCode: string, scope: FileTableSortScope = 'basic'): 'name' | 'size' {
    return this.fileSortState().get(this.getSortStateKey(localeCode, scope))?.column ?? 'name';
  }

  hasSortableSize(files: TranslatedFileDisplayItem[]): boolean {
    return files.some((file) => this.isValidDisplaySize(file.displaySize));
  }

  private getSortStateKey(localeCode: string, scope: FileTableSortScope): string {
    return `${localeCode}::${scope}`;
  }

  async downloadFile(file: TranslatedFileDisplayItem, localeId?: string): Promise<void> {
    if (!file.isDelivered) return;

    const locale = localeId ?? file.localeId;

    try {
      const s3Key = file.isDtpFile
        ? file.translatedFileUploadName
        : file.uploadName;

      if (!s3Key) {
        this.toastService.show('Unable to download file. Please try again.', { type: 'error' });
        return;
      }

      const { url } = await this.s3FilesService.getPresignedUrlForFile(
        s3Key,
        S3DestinationFolder.TRANSLATION,
        true,
        this.detail().jobUid,
        TranslationFileType.TARGET,
      );

      const response = await fetch(url);
      const blob = await response.blob();

      if (file.isDtpFile) {
        // DTP files: vendor uploaded the final file — save it as-is
        this.triggerBlobDownload(blob, s3Key);
      } else {
        // Basic files: the S3 .zip contains a single folder with the actual file — extract it
        const extractedFile = await this.extractFileFromZip(blob);
        if (extractedFile) {
          const downloadName = locale
            ? this.appendLocaleToFileName(extractedFile.name, locale)
            : extractedFile.name;
          this.triggerBlobDownload(extractedFile.blob, downloadName);
        } else {
          // Fallback: deliver the .zip as-is if extraction fails
          const rawName = file.originalName ?? file.uploadName ?? file.name;
          const baseName = rawName.includes('.') ? rawName.substring(0, rawName.indexOf('.')) : rawName;
          this.triggerBlobDownload(blob, `${baseName}.zip`);
        }
      }
    } catch {
      this.toastService.show('Unable to download file. Please try again.', { type: 'error' });
    }
  }

  async downloadAllFiles(): Promise<void> {
    if (!this.hasAnyDeliveredFiles() || this.isDownloadingAll()) return;

    this.isDownloadingAll.set(true);
    try {
      const saveName = this.detail().customJobName ?? this.detail().jobUid ?? 'translated-files';
      const zipBlob = await this.buildDownloadAllZip(saveName);
      if (!zipBlob) {
        this.toastService.show('No eligible files available for download.', { type: 'error' });
        return;
      }

      this.triggerBlobDownload(zipBlob, `${saveName}.zip`);
    } catch {
      this.toastService.show('Unable to download files. Please try again.', { type: 'error' });
    } finally {
      this.isDownloadingAll.set(false);
    }
  }

  private getDownloadCandidates(): DownloadCandidate[] {
    const files = this.detail().files ?? [];
    const selected: DownloadCandidate[] = [];

    for (const file of files) {
      if (file.fileType === 'attachment') continue;
      if (!file.localeId) continue;

      const isDtpCandidate = Boolean(
        file.isDtpFileRevised ??
        file.isDtpFile ??
        (file as VendorOrderFile & { isDtpJob?: boolean }).isDtpJob,
      );
      const key = isDtpCandidate
        ? file.translatedFileUploadName
        : file.uploadName;

      if (!key) continue;

      selected.push({
        localeId: file.localeId,
        s3Key: key,
        originalName: file.originalName ?? file.name,
      });
    }

    return selected;
  }

  private async buildDownloadAllZip(rootName: string): Promise<Blob | null> {
    const candidates = this.getDownloadCandidates();
    if (candidates.length === 0) {
      return null;
    }

    const archive = new JSZip();
    const root = archive.folder(rootName)!;
    const localeNameUsage = new Map<string, Set<string>>();
    let includedCount = 0;

    for (const candidate of candidates) {
      const { url } = await this.s3FilesService.getPresignedUrlForFile(
        candidate.s3Key,
        S3DestinationFolder.TRANSLATION,
        true,
        this.detail().jobUid,
        TranslationFileType.TARGET,
      );

      let fileBlob: Blob;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          continue;
        }
        fileBlob = await response.blob();
      } catch {
        continue;
      }

      const localeFolder = root.folder(candidate.localeId);
      if (!localeFolder) {
        continue;
      }

      if (candidate.s3Key.toLowerCase().endsWith('.zip')) {
        const extractedCount = await this.addExtractedZipEntries(
          localeFolder,
          fileBlob,
          candidate,
          localeNameUsage,
        );
        includedCount += extractedCount;
        continue;
      }

      const ext = this.splitFileName(candidate.s3Key).extension;
      const targetName = this.buildRenamedFileName(candidate.originalName, ext);
      const uniqueName = this.getUniqueNameForLocale(
        candidate.localeId,
        targetName,
        localeNameUsage,
      );
      localeFolder.file(uniqueName, fileBlob);
      includedCount += 1;
    }

    if (includedCount === 0) {
      return null;
    }

    return archive.generateAsync({ type: 'blob' });
  }

  private async addExtractedZipEntries(
    localeFolder: JSZip,
    zipBlob: Blob,
    candidate: DownloadCandidate,
    localeNameUsage: Map<string, Set<string>>,
  ): Promise<number> {
    let count = 0;
    const zip = await JSZip.loadAsync(zipBlob);

    const entries: JSZipObject[] = [];
    zip.forEach((_, entry) => {
      if (!entry.dir) {
        entries.push(entry);
      }
    });

    for (const entry of entries) {
      const entryBlob = await entry.async('blob');
      const ext = this.splitFileName(entry.name).extension;
      const targetName = this.buildRenamedFileName(candidate.originalName, ext);
      const uniqueName = this.getUniqueNameForLocale(
        candidate.localeId,
        targetName,
        localeNameUsage,
      );
      localeFolder.file(uniqueName, entryBlob);
      count += 1;
    }

    return count;
  }

  private splitFileName(fileName: string): { base: string; extension: string } {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot <= 0) {
      return { base: fileName, extension: '' };
    }
    return {
      base: fileName.substring(0, lastDot),
      extension: fileName.substring(lastDot),
    };
  }

  private buildRenamedFileName(originalName: string, extension: string): string {
    const { base } = this.splitFileName(originalName);
    return `${base}${extension}`;
  }

  private getUniqueNameForLocale(
    localeId: string,
    fileName: string,
    localeNameUsage: Map<string, Set<string>>,
  ): string {
    const usedNames = localeNameUsage.get(localeId) ?? new Set<string>();
    localeNameUsage.set(localeId, usedNames);

    if (!usedNames.has(fileName)) {
      usedNames.add(fileName);
      return fileName;
    }

    const { base, extension } = this.splitFileName(fileName);
    let index = 2;
    let nextName = `${base} (${index})${extension}`;

    while (usedNames.has(nextName)) {
      index += 1;
      nextName = `${base} (${index})${extension}`;
    }

    usedNames.add(nextName);
    return nextName;
  }

  private async extractFileFromZip(blob: Blob): Promise<{ name: string; blob: Blob } | null> {
    try {
      const zip = await JSZip.loadAsync(blob);
      const entries = Object.values(zip.files).filter(entry => !entry.dir);
      if (entries.length === 0) return null;

      // The zip contains a single folder with the actual file inside
      const fileEntry = entries[0];
      const fileBlob = await fileEntry.async('blob');
      // Strip any folder prefix from the path to get the filename
      const name = fileEntry.name.includes('/')
        ? fileEntry.name.substring(fileEntry.name.lastIndexOf('/') + 1)
        : fileEntry.name;
      return { name, blob: fileBlob };
    } catch {
      return null;
    }
  }

  private triggerBlobDownload(blob: Blob, downloadName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = downloadName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
  }

  private appendLocaleToFileName(fileName: string, localeId: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1) return `${fileName}_${localeId}`;
    return `${fileName.substring(0, dotIndex)}_${localeId}${fileName.substring(dotIndex)}`;
  }

  private hasValue(value: string | null | undefined): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private resolveDtpDisplayName(
    translatedFileUploadName: string | null | undefined,
    originalName: string | null | undefined,
    localeId: string,
    fallbackName: string,
  ): string {
    if (this.hasValue(translatedFileUploadName)) {
      return this.stripLocaleSuffixFromFileName(translatedFileUploadName.trim(), localeId);
    }

    if (this.hasValue(originalName)) {
      return originalName.trim();
    }

    return fallbackName;
  }

  private stripLocaleSuffixFromFileName(fileName: string, localeId: string): string {
    const { base, extension } = this.splitFileName(fileName);
    const escapedLocale = localeId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const localeSuffixPattern = new RegExp(`[_\\-.]${escapedLocale}$`, 'i');
    const normalizedBase = base.replace(localeSuffixPattern, '');
    return `${normalizedBase}${extension}`;
  }


  private shouldUseRevisedClassification(
    jobStatus: string,
    hasVendor: boolean,
  ): boolean {
    if (!QUALIFYING_STATUSES_FOR_REVISED_DTP.has(jobStatus)) {
      return false;
    }
    if (jobStatus === 'CANCELLED' && !hasVendor) {
      return false;
    }
    return true;
  }

  private sortFilesByName(
    files: TranslatedFileDisplayItem[],
    descending: boolean,
  ): TranslatedFileDisplayItem[] {
    return [...files].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return descending ? -cmp : cmp;
    });
  }

  private sortFilesBySize(
    files: TranslatedFileDisplayItem[],
    descending: boolean,
  ): TranslatedFileDisplayItem[] {
    return [...files].sort((a, b) => {
      const sizeA = this.parseSizeToBytes(a.displaySize);
      const sizeB = this.parseSizeToBytes(b.displaySize);
      const cmp = sizeA - sizeB;
      return descending ? -cmp : cmp;
    });
  }

  private parseSizeToBytes(size: string | null): number {
    if (!size) return 0;
    const match = size.match(/^([\d.]+)\s*(B|KB|MB|GB)$/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    const multipliers: Record<string, number> = { B: 1, KB: 1024, MB: 1048576, GB: 1073741824 };
    return value * (multipliers[unit] ?? 1);
  }

  private isValidDisplaySize(size: string | null): boolean {
    if (!size) return false;
    return /^([\d.]+)\s*(B|KB|MB|GB)$/i.test(size);
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);
    return `${size < 10 && i > 0 ? size.toFixed(2) : size < 100 && i > 0 ? size.toFixed(1) : Math.round(size)} ${units[i]}`;
  }

  private buildFileItems(
    langFiles: VendorOrderFile[],
    useRevised: boolean,
    s3Files: Map<string, { displaySize: string }>,
    localeId: string,
  ): { basicFiles: TranslatedFileDisplayItem[]; dtpFiles: TranslatedFileDisplayItem[]; deliveredCount: number } {
    const basicFiles: TranslatedFileDisplayItem[] = [];
    const dtpFiles: TranslatedFileDisplayItem[] = [];
    let deliveredCount = 0;

    for (const file of langFiles) {
      const isDtp = useRevised
        ? (file.isDtpFileRevised ?? file.isDtpFile ?? false)
        : (file.isDtpFile ?? false);
      const isDtpRevised = useRevised && file.isDtpFileRevised === true;

      const s3Key = isDtp ? file.translatedFileUploadName : file.uploadName;
      const s3Entry = s3Key ? s3Files.get(s3Key) : undefined;

      const isBasicUnchanged = !isDtp &&
        this.hasValue(file.originalName) &&
        this.hasValue(file.uploadName) &&
        file.originalName === file.uploadName;

      let isDelivered: boolean;
      if (isBasicUnchanged) {
        isDelivered = false;
      } else if (isDtpRevised) {
        isDelivered = file.status === 'COMPLETED';
      } else {
        isDelivered = !!s3Entry;
      }
      if (isDelivered) deliveredCount++;

      const item: TranslatedFileDisplayItem = {
        id: file.id ?? '',
        localeId: file.localeId ?? localeId,
        name: isDtp
          ? this.resolveDtpDisplayName(
            file.translatedFileUploadName,
            file.originalName,
            file.localeId ?? localeId,
            file.name,
          )
          : file.name,
        isDelivered,
        displaySize: s3Entry?.displaySize ?? null,
        isDtpFile: isDtp,
        translatedFileUploadName: file.translatedFileUploadName ?? null,
        uploadName: file.uploadName ?? null,
        originalName: file.originalName ?? null,
      };

      if (isDtp) {
        dtpFiles.push(item);
      } else {
        basicFiles.push(item);
      }
    }

    return { basicFiles, dtpFiles, deliveredCount };
  }

  private async markJobAsCompleted(): Promise<void> {
    try {
      await this.vendorOrdersService.markJobAsCompleted(this.detail().jobUid!);
      this.jobCompleted.emit();
    } catch {
      this.toastService.show('Unable to update job status. Please try again.', { type: 'error' });
    } finally {
      this.isMarkingCompletion.set(false);
    }
  }
}
